import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { HiCheck, HiXMark, HiExclamationTriangle } from 'react-icons/hi2';
import { FaGoogleDrive } from 'react-icons/fa';
import { TbBrandOnedrive } from 'react-icons/tb';
import { useGetImportJobQuery, documentsApi } from '@/store/documentsApi';
import { tenantApi } from '@/store/tenantApi';
import { TAG_DOCUMENT, TAG_TENANT } from '@/store/tagTypes';
import { ImportJobStatus, ImportSource } from '@/types/enums';
import {
  ImportProgressCard,
  ImportProgressHeader,
  ImportProgressTitle,
  ImportProgressBar,
  ImportProgressBarFill,
} from './ImportProgress.styled';

interface ImportProgressProps {
  importJobId: string;
}

function isTerminalStatus(status?: string): boolean {
  return status === ImportJobStatus.Completed
    || status === ImportJobStatus.Failed
    || status === ImportJobStatus.PartiallyFailed;
}

export default function ImportProgress({ importJobId }: ImportProgressProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: job } = useGetImportJobQuery(importJobId);
  const isTerminal = job ? isTerminalStatus(job.status) : false;

  // Poll while not terminal
  useGetImportJobQuery(importJobId, {
    pollingInterval: 2000,
    skip: isTerminal,
  });

  // Invalidate caches when job reaches terminal status
  useEffect(() => {
    if (isTerminal) {
      dispatch(documentsApi.util.invalidateTags([{ type: TAG_DOCUMENT, id: 'LIST' }]));
      dispatch(tenantApi.util.invalidateTags([TAG_TENANT]));
    }
  }, [isTerminal, dispatch]);

  if (!job) return null;

  const percent = job.totalFiles > 0
    ? ((job.processedFiles + job.failedFiles) / job.totalFiles) * 100
    : 0;

  const variant = job.status === ImportJobStatus.Completed
    ? 'success'
    : job.status === ImportJobStatus.Failed
      ? 'danger'
      : job.status === ImportJobStatus.PartiallyFailed
        ? 'warning'
        : 'primary';

  const SourceIcon = job.source === ImportSource.GoogleDrive ? FaGoogleDrive : TbBrandOnedrive;

  const statusText = isTerminal
    ? job.status === ImportJobStatus.Completed
      ? t('import.importComplete')
      : job.status === ImportJobStatus.PartiallyFailed
        ? t('import.importPartiallyFailed')
        : t('import.importFailed')
    : t('import.importing', { count: job.totalFiles });

  return (
    <ImportProgressCard>
      <ImportProgressHeader>
        <ImportProgressTitle>
          <SourceIcon />
          {statusText}
        </ImportProgressTitle>
        <span>
          {!isTerminal && <Spinner animation="border" size="sm" />}
          {job.status === ImportJobStatus.Completed && <HiCheck color="var(--bs-success)" />}
          {job.status === ImportJobStatus.Failed && <HiXMark color="var(--bs-danger)" />}
          {job.status === ImportJobStatus.PartiallyFailed && <HiExclamationTriangle color="var(--bs-warning)" />}
        </span>
      </ImportProgressHeader>
      <small className="text-muted">
        {t('import.filesProcessed', { processed: job.processedFiles, total: job.totalFiles })}
        {job.failedFiles > 0 && ` · ${t('import.filesFailed', { failed: job.failedFiles })}`}
      </small>
      <ImportProgressBar>
        <ImportProgressBarFill $percent={percent} $variant={variant} />
      </ImportProgressBar>
    </ImportProgressCard>
  );
}
